import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth, transformResponses } from 'src/app-redux/rtkq.interceptor';
import { vehiclesTag } from 'src/app-redux/rtkq.tags';
import { apiResources } from 'src/common/Constants';
import type {
  TVehicleAvailability,
  TVehicleAvailabilityRequestDTO,
  TVehicleAvailabilityToggleDTO,
} from 'src/common/types/vehicleAvailability.type';
import { Vehicle } from 'src/management/Vehicle';
import type { TSearchFilter, TSearchResponse } from 'src/typings/app.type';

/** RTK Query API slice for vehicle management endpoints. */
export const vehiclesApi = createApi({
  reducerPath: 'vehiclesApi',
  tagTypes: [vehiclesTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch vehicles with pagination. */
    fetchVehicles: builder.query<Vehicle[], number>({
      query: (page) => `${apiResources.vehicle}?page=${page}`,
      providesTags: [vehiclesTag],
      ...transformResponses,
    }),
    /** Query to search vehicles by registration number. */
    searchVehiclesByRegistrationNumber: builder.query<Vehicle[], string>({
      query: (registrationNumber) => `${apiResources.vehicle}/search?registrationNumber=${registrationNumber}`,
      providesTags: [vehiclesTag],
      ...transformResponses,
    }),
    /** Query to find a vehicle by registration number. */
    findVehicleByRegistrationNumber: builder.query<Vehicle, string>({
      query: (registrationNumber) => `${apiResources.vehicle}/registration/${registrationNumber}`,
      ...transformResponses,
    }),
    /** Mutation to create a new vehicle. */
    createVehicle: builder.mutation<void, Vehicle>({
      query: (vehicle) => ({
        url: apiResources.vehicle,
        method: 'POST',
        body: vehicle,
      }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [vehiclesTag]),
    }),
    /** Mutation to update an existing vehicle. */
    updateVehicle: builder.mutation<void, Vehicle>({
      query: (vehicle) => ({
        url: apiResources.vehicle,
        method: 'PUT',
        body: vehicle,
      }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [vehiclesTag]),
    }),
    /** Mutation to assign a driver to a vehicle. */
    assignDriverToVehicle: builder.mutation<void, { driverId: number; vehicleId: number }>({
      query: ({ driverId, vehicleId }) => ({
        url: `${apiResources.vehicle}/${vehicleId}/driver/${driverId}/assign`,
        method: 'POST',
      }),
      ...transformResponses,
    }),
    getAvailability: builder.query<
      TSearchResponse<TVehicleAvailability>,
      TSearchFilter & TVehicleAvailabilityRequestDTO
    >({
      providesTags: [vehiclesTag],
      query: (params) => ({ url: `${apiResources.vehicle}/availability`, params }),
      ...transformResponses,
    }),
    toggleVehicleAvailability: builder.mutation<string, TVehicleAvailabilityToggleDTO>({
      query: (body) => ({ url: `${apiResources.vehicle}/availability`, method: 'PUT', body }),
      invalidatesTags: (_result, error) => (error ? [] : [vehiclesTag]),
      ...transformResponses,
    }),
  }),
});

/** RTK Query hooks for vehicle management endpoints. */
export const {
  useFetchVehiclesQuery,
  useSearchVehiclesByRegistrationNumberQuery,
  useLazyFindVehicleByRegistrationNumberQuery,
  useLazySearchVehiclesByRegistrationNumberQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useAssignDriverToVehicleMutation,
  useGetAvailabilityQuery,
  useToggleVehicleAvailabilityMutation,
} = vehiclesApi;
